import React, { useState, useRef } from "react";
import { Mic, Square, Sparkles, Loader2, RefreshCw, Wand2, Check } from "lucide-react";
import { motion } from "framer-motion";
import { API } from "./api";
import { SoundWave } from "./SoundWave";

// Boîte blanche façon ChatGPT : saisie texte + dictée vocale (Whisper).
export function VoiceTextBox({ value, onChange, placeholder, testid = "voice-text-box", minHeight = "min-h-[120px]", micLabel = "Dicter" }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [err, setErr] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const startRec = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "presentation.webm");
          const res = await fetch(`${API}/ai/transcribe`, { method: "POST", body: fd });
          if (!res.ok) throw new Error("transcription");
          const d = await res.json();
          onChange((value ? value + " " : "") + (d.text || ""));
        } catch (_) { setErr("Transcription impossible, réessayez ou saisissez au clavier."); }
        setTranscribing(false);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (_) { setErr("Micro indisponible. Autorisez l'accès au micro ou saisissez au clavier."); }
  };

  const stopRec = () => { if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); } };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-zinc-200 p-4" data-testid={testid}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`${testid}-textarea`}
        className={`w-full ${minHeight} resize-none bg-transparent text-zinc-800 placeholder-zinc-400 outline-none text-sm leading-relaxed`}
      />
      <div className="flex items-center justify-between pt-2 border-t border-zinc-100 mt-1">
        <span className="text-[11px] text-zinc-400 inline-flex items-center gap-2">{transcribing ? "Transcription en cours…" : recording ? <><SoundWave className="text-red-500" /> Enregistrement…</> : "Écrivez ou dictez au micro"}</span>
        {!recording ? (
          <motion.button type="button" onClick={startRec} disabled={transcribing} data-testid={`${testid}-mic`}
            animate={{ scale: [1, 1.06, 1], y: [0, -2, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            whileHover={{ scale: 1.08 }}
            style={{ boxShadow: "0 0 16px rgba(193,154,107,0.55)" }}
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider bg-[#C19A6B] hover:bg-[#A88358] text-white rounded-full px-5 py-2.5 transition-colors disabled:opacity-50">
            {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />} {micLabel}
          </motion.button>
        ) : (
          <button type="button" onClick={stopRec} data-testid={`${testid}-stop`}
            className="inline-flex items-center gap-2 text-xs font-medium bg-red-500 hover:bg-red-600 text-white rounded-full px-3.5 py-2 transition-colors">
            <SoundWave className="text-white" bars={5} /> Arrêter
          </button>
        )}
      </div>
      {err && <p className="text-[11px] text-red-500 mt-2">{err}</p>}
    </div>
  );
}

// Assistant de présentation conseiller : génère Présentation + Vision du métier.
export function AgentPresenter({ context = {}, description, vision, onGenerated }) {
  const [raw, setRaw] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const hasResult = !!(description || vision);

  const generate = async () => {
    setErr(""); setLoading(true);
    try {
      const res = await fetch(`${API}/ai/agent-presentation`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: raw, ...context }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Erreur");
      onGenerated({ description: d.description, vision: d.vision });
    } catch (e) { setErr(typeof e.message === "string" ? e.message : "Génération impossible"); }
    setLoading(false);
  };

  return (
    <div className="space-y-3 border border-[#C19A6B]/30 bg-[#C19A6B]/5 p-4 rounded-lg" data-testid="agent-presenter">
      <div className="flex items-center gap-2 text-sm text-[#C19A6B]"><Sparkles className="w-4 h-4" /> Laissez l'IA rédiger votre présentation</div>
      <p className="text-xs text-zinc-400">Présentez-vous librement (parcours, valeurs, secteur…). L'IA génère votre <b className="text-zinc-200">présentation</b> et votre <b className="text-zinc-200">vision du métier</b>.</p>
      <VoiceTextBox value={raw} onChange={setRaw} placeholder="Ex : Conseiller depuis 12 ans dans l'Oise, passionné par les biens de caractère et l'accompagnement humain…" testid="agent-voice" />
      <button type="button" onClick={generate} disabled={loading || raw.trim().length < 10} data-testid="agent-generate"
        className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white text-sm font-medium uppercase tracking-wider px-5 py-2.5 transition-colors">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : hasResult ? <RefreshCw className="w-4 h-4" /> : <Wand2 className="w-4 h-4" />}
        {loading ? "Rédaction en cours…" : hasResult ? "Régénérer une version" : "Générer une présentation au top"}
      </button>
      {err && <p className="text-xs text-red-400">{err}</p>}

      {hasResult && !loading && (
        <div className="space-y-3 pt-1" data-testid="agent-result">
          <div className="flex items-center gap-1.5 text-xs text-green-400"><Check className="w-3.5 h-3.5" /> Présentation générée — modifiable ci-dessous</div>
          {description && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wider text-[#C19A6B] mb-1">Présentation</p>
              <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-line">{description}</p>
            </div>
          )}
          {vision && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wider text-[#C19A6B] mb-1">Votre vision du métier</p>
              <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-line">{vision}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
